
import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useLocation } from 'react-router-dom';
import {
  getFlow,
  saveDefinition as apiSaveDefinition,
  patchFlow,
  validateFlow,
} from 'services/chatbotFlowService';
import { createNode, getNodeMeta } from '../constants/nodeTypes';
import { normalizeFlowDefinition } from '../utils/flowButtons';
import { saveDraftStore, loadDraftStore, clearDraftStore } from '../utils/flowDraftStore';

/**
 * Returns the primary outgoing edge event for a given node type.
 * Used for "Next Step" wiring and insertion logic.
 */
export function getOutgoingEvent(nodeType) {
  if (!nodeType) return 'completed';
  if (nodeType.startsWith('trigger.')) return 'matched';
  switch (nodeType) {
    case 'logic.ask': return 'user_input';
    case 'logic.validate': return 'valid';
    case 'logic.condition': return 'yes'; // branching, but this is the primary
    case 'integration.api': return 'yes'; // branching, but this is the primary
    default: return 'completed';
  }
}

/**
 * Node types that have their own built-in branching / routing config.
 * These do NOT get the generic "Next Step" dropdown in the drawer.
 */
export const TYPES_WITH_OWN_ROUTING = new Set([
  'logic.condition',
  'integration.api',
  'logic.route_by_var',
  'ai.classify',
  'message.list_dynamic',
  'message.list_preset',
  'message.template',
  'message.interactive_buttons',
]);

const defaultDefinition = () => ({
  flow_id: 'new-flow',
  version: 1,
  entry: { trigger_evaluation: 'first_match' },
  nodes: [createNode('trigger.keyword', 'trg-default', { x: 0, y: 0 })],
  edges: [],
});

export default function useFlowEditor(flowId) {
  const [flow, setFlow] = useState(null);
  const [definition, setDefinition] = useState(defaultDefinition());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [validation, setValidation] = useState({ valid: true, errors: [], warnings: [] });
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const hasChangesRef = useRef(false);
  const saveTimeoutRef = useRef(null);

  const pushHistory = useCallback((def) => {
    const snap = JSON.stringify(def);
    const hist = historyRef.current.slice(0, historyIndexRef.current + 1);
    hist.push(snap);
    if (hist.length > 50) hist.shift();
    historyRef.current = hist;
    historyIndexRef.current = hist.length - 1;
  }, []);

  const loadFlow = useCallback(async () => {
    if (!flowId) return;
    setLoading(true);
    try {
      const res = await getFlow(flowId);
      const data = res?.data || res;
      setFlow(data);

      const draft = await loadDraftStore(flowId);
      const apiTime = data?.updated_at ? new Date(data.updated_at).getTime() : 0;

      let def;
      if (draft && draft.updatedAt > apiTime) {
        def = normalizeFlowDefinition(draft.definition);
        setHasRestoredDraft(true);
      } else {
        def = normalizeFlowDefinition(data?.definition || defaultDefinition());
        setHasRestoredDraft(false);
      }

      setDefinition(def);
      pushHistory(def);
      hasChangesRef.current = false;
    } catch (e) {
      message.error('Failed to load flow');
    } finally {
      setLoading(false);
    }
  }, [flowId, pushHistory]);

  useEffect(() => {
    loadFlow();
  }, [loadFlow]);

  const location = useLocation();
  const initialPathRef = useRef(location.pathname);

  useEffect(() => {
    initialPathRef.current = location.pathname;
  }, [flowId, location.pathname]);

  useEffect(() => {
    return () => {
      if (flowId && window.location.pathname !== initialPathRef.current) {
        clearDraftStore(flowId);
      }
    };
  }, [flowId]);

  useEffect(() => {
    if (!hasChangesRef.current || !flowId) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      await saveDraftStore(flowId, definition);
    }, 200);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [definition, flowId]);

  const updateDefinition = useCallback(
    (updater) => {
      hasChangesRef.current = true;
      setDefinition((prev) => {
        const raw = typeof updater === 'function' ? updater(prev) : updater;
        // Normalize on every mutation so spurious edges are cleaned up in real-time
        const next = normalizeFlowDefinition(raw);
        pushHistory(next);
        return next;
      });
    },
    [pushHistory]
  );

  const selectNode = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
    setDrawerOpen(!!nodeId);
  }, []);

  const updateNodeConfig = useCallback(
    (nodeId, config) => {
      updateDefinition((prev) => ({
        ...prev,
        nodes: (prev.nodes || []).map((n) =>
          n.id === nodeId ? { ...n, config: { ...n.config, ...config } } : n
        ),
      }));
    },
    [updateDefinition]
  );

  const updateNodeMeta = useCallback(
    (nodeId, patch) => {
      updateDefinition((prev) => ({
        ...prev,
        nodes: (prev.nodes || []).map((n) =>
          n.id === nodeId ? { ...n, ...patch, config: { ...n.config, ...(patch.config || {}) } } : n
        ),
      }));
    },
    [updateDefinition]
  );

  const addNode = useCallback(
    (type, afterNodeId = null, event = 'completed') => {
      const newNode = createNode(type, `node-${Date.now()}`, {
        x: 100,
        y: (definition.nodes?.length || 0) * 80,
      });
      updateDefinition((prev) => {
        const nodes = [...(prev.nodes || []), newNode];
        let edges = [...(prev.edges || [])];
        if (afterNodeId) {
          const existing = edges.find(
            (e) => e.from === afterNodeId && (e.event || 'completed') === event
          );
          const previousTo = existing?.to;
          edges = edges.filter((e) => !(e.from === afterNodeId && (e.event || 'completed') === event));
          edges.push({ from: afterNodeId, to: newNode.id, event });
          // Keep former "Then continue" target linked after the new step (do not orphan it).
          if (previousTo) {
            edges.push({ from: newNode.id, to: previousTo, event: 'completed' });
          }
          // If a button_reply edge was added, remove any completed edge from the same
          // source to the same new target to avoid dual-path duplicates.
          if (event === 'button_reply') {
            edges = edges.filter(
              (e) => !(e.from === afterNodeId && e.event === 'completed' && e.to === newNode.id)
            );
          }
        }
        return { ...prev, nodes, edges };
      });
      selectNode(newNode.id);
      return newNode.id;
    },
    [definition.nodes?.length, updateDefinition, selectNode]
  );

  const changeNodeType = useCallback(
    (nodeId, newType) => {
      const meta = getNodeMeta(newType);
      updateDefinition((prev) => ({
        ...prev,
        nodes: (prev.nodes || []).map((n) =>
          n.id === nodeId
            ? { ...n, type: newType, config: JSON.parse(JSON.stringify(meta.defaultConfig || {})) }
            : n
        ),
      }));
    },
    [updateDefinition]
  );

  const deleteNode = useCallback(
    (nodeId) => {
      updateDefinition((prev) => {
        const edges = prev.edges || [];

        // Find all incoming edges (parents) and outgoing edges (children)
        const incomingEdges = edges.filter((e) => e.to === nodeId);
        const outgoingEdges = edges.filter((e) => e.from === nodeId);

        // Remove all edges connected to the deleted node
        let newEdges = edges.filter((e) => e.from !== nodeId && e.to !== nodeId);

        // Reconnect: for each parent → deleted node → child, create parent → child
        incomingEdges.forEach((inEdge) => {
          outgoingEdges.forEach((outEdge) => {
            // Create a new edge from parent to child, preserving the parent's event type
            newEdges.push({
              from: inEdge.from,
              to: outEdge.to,
              event: inEdge.event,
              ...(inEdge.match ? { match: inEdge.match } : {}),
              ...(inEdge.capture ? { capture: inEdge.capture } : {}),
            });
          });
        });

        return {
          ...prev,
          nodes: (prev.nodes || []).filter((n) => n.id !== nodeId),
          edges: newEdges,
        };
      });
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null);
        setDrawerOpen(false);
      }
    },
    [updateDefinition, selectedNodeId]
  );

  /**
   * Remove only the edge from `fromNodeId` → `toNodeId` without deleting the node.
   * Used when the user clicks Delete on a back-reference (secondary occurrence) of a node.
   * The node itself stays intact; only this one incoming link is severed.
   */
  const disconnectEdge = useCallback(
    (fromNodeId, toNodeId) => {
      updateDefinition((prev) => ({
        ...prev,
        edges: (prev.edges || []).filter(
          (e) => !(e.from === fromNodeId && e.to === toNodeId)
        ),
      }));
    },
    [updateDefinition]
  );

  const connectNodes = useCallback(
    (from, to, event = 'completed', match = null) => {
      updateDefinition((prev) => {
        const prevEdges = prev.edges || [];

        // 1. Find the old target being replaced (for splice)
        const oldEdge = prevEdges.find(
          (e) => e.from === from && e.event === event &&
            (!match || JSON.stringify(e.match) === JSON.stringify(match))
        );
        const oldTo = oldEdge?.to;

        // 2. Remove the old edge
        let edges = prevEdges.filter(
          (e) => !(e.from === from && e.event === event &&
            (!match || JSON.stringify(e.match) === JSON.stringify(match)))
        );

        // 3. Add the new edge
        edges.push({ from, to, event, ...(match ? { match } : {}) });

        // 4. Auto-splice: if oldTo exists and the new target (to) has no
        //    outgoing edge yet, reconnect oldTo after the new target so
        //    the flow chain isn't broken.
        if (oldTo && oldTo !== to) {
          const newTargetNode = (prev.nodes || []).find((n) => n.id === to);
          const continuationEvent = newTargetNode
            ? getOutgoingEvent(newTargetNode.type)
            : 'completed';
          const hasOutgoing = edges.some(
            (e) => e.from === to && e.event === continuationEvent
          );
          if (!hasOutgoing) {
            edges.push({ from: to, to: oldTo, event: continuationEvent });
          }
        }

        // 5. Remove redundant opposite edges to the same target
        if (event === 'button_reply') {
          edges = edges.filter(
            (e) => !(e.from === from && e.event === 'completed' && e.to === to)
          );
        } else if (event === 'completed') {
          edges = edges.filter(
            (e) => !(e.from === from && e.event === 'button_reply' && e.to === to)
          );
        }
        return { ...prev, edges };
      });
    },
    [updateDefinition]
  );

  /**
   * Move a node from its current position to before or after another node.
   * position: 'after' (default) | 'before'
   * Handles all edge rewiring:
   *  1. Extract: disconnect node from old position, reconnect parent → child
   *  2. Insert: splice node at the desired position
   */
  const moveNode = useCallback(
    (nodeId, relativeToId, position = 'after') => {
      if (nodeId === relativeToId) return;
      updateDefinition((prev) => {
        const nodes = prev.nodes || [];
        let edges = [...(prev.edges || [])];
        const draggedNode = nodes.find((n) => n.id === nodeId);
        const relativeNode = nodes.find((n) => n.id === relativeToId);
        if (!draggedNode || !relativeNode) return prev;

        // When position === 'before': we want to insert BEFORE relativeToId.
        // That means insert AFTER relativeToId's parent (the node that points to relativeToId).
        // Find the actual anchor node — the one we insert AFTER.
        let anchorId = relativeToId;
        if (position === 'before') {
          // Find the primary incoming edge to the relative node (excluding button_reply)
          const parentEdge = edges.find(
            (e) => e.to === relativeToId && e.event !== 'button_reply'
          );
          if (!parentEdge) return prev;
          anchorId = parentEdge.from;
        }

        // Safety: don't move onto itself
        if (nodeId === anchorId) return prev;

        // ── Step 1: Extract the dragged node from its current position ──
        const incomingEdge = edges.find((e) => e.to === nodeId && e.event !== 'button_reply');
        const draggedEvent = getOutgoingEvent(draggedNode.type);
        const outgoingEdge = edges.find((e) => e.from === nodeId && e.event === draggedEvent);

        // Remove both edges connected to the dragged node
        if (incomingEdge) edges = edges.filter((e) => e !== incomingEdge);
        if (outgoingEdge) edges = edges.filter((e) => e !== outgoingEdge);

        // Reconnect: parent → child (bridge the gap left by removing dragged node)
        if (incomingEdge && outgoingEdge) {
          edges.push({
            from: incomingEdge.from,
            to: outgoingEdge.to,
            event: incomingEdge.event,
            ...(incomingEdge.match ? { match: incomingEdge.match } : {}),
            ...(incomingEdge.capture ? { capture: incomingEdge.capture } : {}),
          });
        }
        // If dragged node had no outgoing (was at end), no bridging needed.

        // ── Step 2: Insert dragged node after anchorId ────────────────
        const anchorNode = nodes.find((n) => n.id === anchorId);
        const anchorEvent = anchorNode ? getOutgoingEvent(anchorNode.type) : 'completed';
        // Re-read edges after bridging step to get the updated anchor outgoing edge
        const anchorOutgoing = edges.find((e) => e.from === anchorId && e.event === anchorEvent);

        // Remove anchor's old outgoing edge
        if (anchorOutgoing) edges = edges.filter((e) => e !== anchorOutgoing);

        // Wire: anchor → dragged node
        edges.push({ from: anchorId, to: nodeId, event: anchorEvent });

        // Wire: dragged node → anchor's old child
        if (anchorOutgoing) {
          edges.push({ from: nodeId, to: anchorOutgoing.to, event: draggedEvent });
        }

        return { ...prev, edges };
      });
    },
    [updateDefinition]
  );

  const saveDraft = useCallback(async (silent = false) => {
    if (!flowId) return;
    setSaving(true);
    try {
      const res = await apiSaveDefinition(flowId, normalizeFlowDefinition(definition));
      const data = res?.data || res;
      setFlow((f) => ({ ...f, ...data }));
      setLastSavedAt(new Date());
      await clearDraftStore(flowId);
      setHasRestoredDraft(false);
      if (!silent) {
        message.success('Flow saved');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Save failed';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  }, [flowId, definition]);

  const discardDraft = useCallback(async () => {
    if (!flowId) return;
    await clearDraftStore(flowId);
    setHasRestoredDraft(false);
    await loadFlow();
  }, [flowId, loadFlow]);

  const runValidate = useCallback(async () => {
    if (!flowId) return validation;
    try {
      const res = await validateFlow(flowId);
      const result = {
        valid: res?.valid !== false,
        errors: res?.errors || [],
        warnings: res?.warnings || [],
      };
      setValidation(result);
      return result;
    } catch (e) {
      const data = e?.response?.data;
      const result = {
        valid: false,
        errors: data?.errors || [{ message: 'Validation failed' }],
        warnings: data?.warnings || [],
      };
      setValidation(result);
      return result;
    }
  }, [flowId, validation]);

  const renameFlow = useCallback(
    async (name) => {
      if (!flowId) return;
      const res = await patchFlow(flowId, { name });
      const data = res?.data || res;
      setFlow((f) => ({ ...f, ...data }));
    },
    [flowId]
  );

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    hasChangesRef.current = true;
    historyIndexRef.current -= 1;
    setDefinition(JSON.parse(historyRef.current[historyIndexRef.current]));
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    hasChangesRef.current = true;
    historyIndexRef.current += 1;
    setDefinition(JSON.parse(historyRef.current[historyIndexRef.current]));
  }, []);

  const selectedNode = (definition.nodes || []).find((n) => n.id === selectedNodeId) || null;

  const nodeOptions = (definition.nodes || []).map((n) => {
    const meta = getNodeMeta(n.type);
    const summary = meta.listLabel?.(n.config) || meta.label;
    return {
      value: n.id,
      label: n.config?._label || summary,
      desc: n.config?._label ? summary : '',
    };
  });

  return {
    flow,
    definition,
    loading,
    saving,
    selectedNodeId,
    selectedNode,
    drawerOpen,
    setDrawerOpen,
    validation,
    lastSavedAt,
    nodeOptions,
    loadFlow,
    updateDefinition,
    selectNode,
    updateNodeConfig,
    updateNodeMeta,
    addNode,
    changeNodeType,
    deleteNode,
    disconnectEdge,
    connectNodes,
    moveNode,
    saveDraft,
    runValidate,
    renameFlow,
    undo,
    redo,
    hasRestoredDraft,
    discardDraft,
  };
}
